import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService, User } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { SettingsService, ProjectSettings } from '../../services/settings.service';
import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="min-h-screen transition-colors duration-300" [class.bg-gray-100]="!settings?.backgroundUrl">
      <!-- Navbar -->
      <nav class="bg-white shadow-sm">
        <div class="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div class="flex items-center gap-4">
            <img *ngIf="settings?.logoUrl" [src]="settings?.logoUrl" class="header-logo" alt="Logo">
            <h1 class="text-2xl font-bold text-gray-900">{{ i18n.t('dashboard.title') }}</h1>
          </div>
          <div class="flex items-center gap-4">
            <span class="text-gray-700">{{ currentUser?.username }}</span>
            <button
              (click)="onLogout()"
              class="btn-system-danger py-2! px-4! text-sm!"
            >
              {{ i18n.t('dashboard.logout') }}
            </button>
          </div>
        </div>
      </nav>

      <!-- Main Content -->
      <main class="max-w-7xl mx-auto px-6 py-12">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Welcome Card -->
          <div class="bg-white rounded-lg shadow p-6 border-l-4 border-primary">
            <h2 class="text-xl font-semibold text-gray-900 mb-2">{{ i18n.t('dashboard.welcome') }}</h2>
            <p class="text-gray-600">
              {{ i18n.t('dashboard.welcomeDetail') }}
            </p>
          </div>

          <!-- User Info Card -->
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">{{ i18n.t('dashboard.userInfo') }}</h2>
            <div class="space-y-3 text-sm">
              <div class="flex justify-between border-b pb-2">
                <span class="font-medium text-gray-500">{{ i18n.t('login.username') }}</span>
                <span class="text-gray-900">{{ currentUser?.username }}</span>
              </div>
              <div class="flex justify-between border-b pb-2">
                <span class="font-medium text-gray-500">Email</span>
                <span class="text-gray-900">{{ currentUser?.email }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-medium text-gray-500">ID</span>
                <span class="text-gray-700 font-mono">{{ currentUser?.id }}</span>
              </div>
            </div>
          </div>

          <!-- Authentication Status Card -->
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">{{ i18n.t('dashboard.authStatus') }}</h2>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <div class="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                <span class="text-sm text-gray-600">JWT Token Active</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                <span class="text-sm text-gray-600">Secure Session</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Features Section (Optional/Static for now) -->
        <div class="mt-12 bg-gray-50/50 rounded-2xl p-8 border border-gray-100">
          <h2 class="text-2xl font-bold text-gray-900 mb-8">{{ i18n.t('dashboard.features') }}</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div class="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <h3 class="font-bold text-gray-900 mb-2">Security</h3>
              <p class="text-gray-500 text-xs">Advanced JWT protection</p>
            </div>
            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div class="w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
              </div>
              <h3 class="font-bold text-gray-900 mb-2">Interface</h3>
              <p class="text-gray-500 text-xs">Responsive design</p>
            </div>
            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div class="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              </div>
              <h3 class="font-bold text-gray-900 mb-2">Performance</h3>
              <p class="text-gray-500 text-xs">Optimized SSR assets</p>
            </div>
            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div class="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
              </div>
              <h3 class="font-bold text-gray-900 mb-2">Support</h3>
              <p class="text-gray-500 text-xs">Quick help system</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
})
export class DashboardComponent implements OnInit {
  currentUser: User | null = null;
  settings: ProjectSettings | null = null;

  // Change password fields
  oldPassword = '';
  newPassword = '';
  changingPassword = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private settingsService: SettingsService,
    public i18n: I18nService
  ) { }

  ngOnInit(): void {
    this.authService.currentUser$.subscribe((user) => {
      this.ngZone.run(() => {
        this.currentUser = user;
        this.cdr.detectChanges();
      });
    });

    this.settingsService.settings$.subscribe(settings => {
      this.settings = settings;
      this.cdr.detectChanges();
    });
  }

  onLogout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.ngZone.run(() => {
          this.router.navigate(['/login']);
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        console.error('Logout error:', err);
        this.ngZone.run(() => {
          this.router.navigate(['/login']);
          this.cdr.detectChanges();
        });
      },
    });
  }

  onChangePassword(): void {
    if (!this.oldPassword || !this.newPassword) {
      this.toastService.error(this.i18n.t('login.username') + ' & ' + this.i18n.t('login.password') + ' ' + this.i18n.t('common.required'));
      return;
    }

    if (this.newPassword.length < 6) {
      this.toastService.error(this.i18n.t('toast.passwordError'));
      return;
    }

    this.changingPassword = true;
    this.cdr.detectChanges();

    this.authService.changePassword(this.oldPassword, this.newPassword).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          this.toastService.success(this.i18n.t('toast.passwordSuccess'));
          this.oldPassword = '';
          this.newPassword = '';
          this.changingPassword = false;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          const message = err.error?.error || this.i18n.t('toast.passwordError');
          this.toastService.error(message);
          this.changingPassword = false;
          this.cdr.detectChanges();
        });
      }
    });
  }
}
